import { z } from 'zod';
import { createModel, currencySchema, partySchema, QueryOne, QueryMany } from '../../common';
import { dateSchema, priceSchema } from '../../validation';
import { walletHolderSchema } from './wallet.holder.model';
import { walletStatusSchema } from './wallet.status.model';
import { UTagSharedSchema } from '../utag';

/** `Wallet db model` */
export const WalletSchema = createModel({
  /** the status of the wallet. If active, then we can make transactions to it. */
  status: walletStatusSchema,

  /** the balance equals to all deposits minus all withdrawals. */
  balance: priceSchema,

  /** the currency in which the balance is in */
  currency: currencySchema,

  /** the wallet holder info:
   * - name: Org or Person name if individual
   * - type: carrier company / business customer / freelancer driver / etc
   */
  holder: walletHolderSchema,

  /**
   * The organization that the wallet `holder` belongs to, as a
   * relations-v2 {@link partySchema} snapshot.
   *
   * Phase 2 migration: this used to be a legacy `relationSchema`
   * keyed off the old `Customer` / `Carrier` collections. It now
   * carries the canonical `Party` shape (`_id`, `name`, `target`,
   * `roles[]`, `ids[]`) so:
   *
   *   - per-org wallet lookups can probe `ids[]` and pick the right
   *     wallet whether the source is `org_id`, `client_id`, etc.,
   *   - wallet listings/aggregations can filter by `roles[]` (e.g.
   *     "show me all carrier_company wallets I have access to")
   *     without re-resolving the org doc,
   *   - {@link InvoiceBeneficiaryService.resolveBeneficiaryForParty}
   *     and the per-leg `captureOrderLeg` flow consume the same
   *     shape end-to-end.
   *
   * For non-org holders (`individual_customer` / `freelancer_driver`),
   * this field stays `undefined`.
   */
  org: partySchema.optional(),

  /** the last time there was a transaction as money coming `IN` on this wallet. */
  lastInDate: dateSchema.optional(),

  /** the last time there was a transaction as money `OUT` of this wallet */
  lastOutDate: dateSchema.optional(),
  UTags: z.array(UTagSharedSchema),
});

/** `Wallet db model` */
export type Wallet = z.infer<typeof WalletSchema.model>;
export type WalletData = z.infer<typeof WalletSchema.data>;
export type WalletPartial = z.infer<typeof WalletSchema.partial>;
export type WalletQueryOne = QueryOne<Wallet>;
export type WalletQueryMany = QueryMany<Wallet>;
